import { telaSobDemanda } from '../../nucleo/paginas/TelaSobDemanda';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../nucleo/api';
import type { ModuloWeb } from '../../nucleo/modulos';
import { CartaoIndicador } from '../../nucleo/ui/Estados';
import { dicionario } from './i18n';
import { acaoNovo, ChipEstado, Dinheiro } from './comum';
import type { EstadoCompetencia } from './tipos';
import { periodoTexto } from './tipos';

/**
 * F10: indicador do Início com a competência do mês corrente (ou a mais recente até ele, SM2-R7-03) e o custo total. ROB-01: o cartão é o
 * CartaoIndicador do núcleo (CONTRATOS 7.13): carregando mostra reticências; API em erro mostra o traço e "Tentar de
 * novo" (nunca "..." para sempre nem "Nenhuma competência"). M12.4: com empresas permitidas, o custo é só das empresas
 * visíveis (parcial). SM2-R6-01: quem não é gestor da folha não recebe o custo; o cartão mostra só o mês e o estado.
 */
function Indicadores() {
  const { t, i18n } = useTranslation('folha');
  const q = useQuery({
    queryKey: ['folha', 'indicadores'],
    queryFn: () =>
      api<{ periodo_atual: string; competencia: { id: string; periodo: string; estado: EstadoCompetencia; funcionarios: number; liquido_centavos: string | null; custo_centavos: string | null; moeda: string; parcial?: boolean } | null }>(
        '/api/v1/folha/indicadores',
      ),
  });
  const c = q.data?.competencia;
  const valor = q.isPending
    ? null
    : !c
      ? <span className="text-base font-normal text-tinta-3">{t('indicadores.nenhuma')}</span>
      : c.custo_centavos !== null
        ? <Dinheiro centavos={c.custo_centavos} moeda={c.moeda} />
        : periodoTexto(c.periodo, i18n.language, true);
  const detalhe = c ? (
    <div className="mt-1 flex flex-col gap-1">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        {c.custo_centavos !== null && <span className="font-medium">{periodoTexto(c.periodo, i18n.language, true)}</span>}
        <ChipEstado estado={c.estado} />
      </p>
      <p className="text-xs text-tinta-3">{c.custo_centavos !== null ? `${t('campos.custo')} · ` : ''}{t('textos.pessoas', { count: c.funcionarios })}{c.parcial ? ` · ${t('textos.suasEmpresas')}` : ''}</p>
    </div>
  ) : undefined;
  const cartao = <CartaoIndicador rotulo={t('indicadores.titulo')} valor={valor} consultas={[q]} detalhe={detalhe} />;
  // Só o cartão com dado é link (o botão "Tentar de novo" do erro não pode ficar dentro de um link).
  if (q.isError || q.isPending) return cartao;
  return (
    <Link to={c ? `/pessoas/competencias/${c.id}` : '/pessoas/competencias'} className="block rounded-xl hover:ring-1 hover:ring-acento">
      {cartao}
    </Link>
  );
}

const carregar0 = () => import('./Beneficios');
const carregar1 = () => import('./Eventos');
const carregar2 = () => import('./Competencias');
const carregar3 = () => import('./FichaCompetencia');
const carregar4 = () => import('./Holerites');

const Beneficios = telaSobDemanda(carregar0, 'Beneficios');
const Eventos = telaSobDemanda(carregar1, 'Eventos');
const Competencias = telaSobDemanda(carregar2, 'Competencias');
const FichaCompetencia = telaSobDemanda(carregar3, 'FichaCompetencia');
const Holerites = telaSobDemanda(carregar4, 'Holerites');

export const modulo: ModuloWeb = {
  slug: 'folha',
  menu: 'pessoas',
  dicionario,
  abas: [
    { rotulo: 'abas.beneficios', caminho: 'beneficios', elemento: <Beneficios />, Acao: acaoNovo('folha.criar', 'acoes.novoBeneficio'), ordem: 20 },
    { rotulo: 'abas.eventos', caminho: 'eventos', elemento: <Eventos />, Acao: acaoNovo('folha.criar', 'acoes.novoEvento', 'evento'), ordem: 21 },
    { rotulo: 'abas.competencias', caminho: 'competencias', elemento: <Competencias />, Acao: acaoNovo('folha.criar', 'acoes.abrirCompetencia', '1', true), ordem: 22 },
    { rotulo: 'abas.holerites', caminho: 'holerites', elemento: <Holerites />, ordem: 23 },
  ],
  rotas: [{ path: 'pessoas/competencias/:id', element: <FichaCompetencia /> }],
  Indicadores,
};
